import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  cn,
} from '@seen/ui'
import { isEmpty } from 'lodash-es'
import { type JSX, useState } from 'react'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'

interface ChoiceOptionProps {
  option: string
  display: string | null
  onChange: (newOption: string, shouldUpdateDisplay: boolean) => void
  onPreselect: () => void
  onUnselect: () => void
  onDelete: () => void
  onAddLeft: () => void
  onAddRight: () => void
  hasDropdown: boolean
  onToggleDropdown: () => void
  className?: string
}

export function ChoiceOption({
  option,
  display,
  onChange,
  hasDropdown,
  onToggleDropdown,
  onAddLeft,
  onAddRight,
  onDelete,
  onPreselect,
  onUnselect,
  className,
}: ChoiceOptionProps): JSX.Element {
  // To handle the case where the user preselects an option, then makes it into the empty string, and then types something back in, we need to store whether the option was preselected.
  const isPreselected = !isEmpty(display) && option === display
  const [savedPreselected, setSavedPreselected] = useState(false)
  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <RichTextEditor
          autofocus={option === ''}
          variant="p"
          contentClassName={cn(
            'bg-muted px-1 text-center font-medium hover:brightness-90 focus-visible:z-10 focus-visible:rounded focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:brightness-100',
            isPreselected && 'text-blue-600',
            option === '' && 'min-w-10',
            className
          )}
          value={option}
          onFocus={() => {
            setSavedPreselected(isPreselected)
          }}
          onBlur={() => {
            setSavedPreselected(false)
          }}
          onChange={(newOption) => {
            onChange(newOption, isPreselected || savedPreselected)
          }}
        />
      </ContextMenuTrigger>
      <ContextMenuContent>
        {!isPreselected && (
          <ContextMenuItem onSelect={onPreselect}>Preselect</ContextMenuItem>
        )}
        {isPreselected && (
          <ContextMenuItem onSelect={onUnselect}>Unselect</ContextMenuItem>
        )}
        <ContextMenuItem onSelect={onDelete}>Delete</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={onAddLeft}>
          Add choice to the left
        </ContextMenuItem>
        <ContextMenuItem onSelect={onAddRight}>
          Add choice to the right
        </ContextMenuItem>
        <ContextMenuItem onSelect={onToggleDropdown}>
          {hasDropdown
            ? 'Remove dropdown and all its choices'
            : 'Add a dropdown'}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}
